import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getBusinessTransaction } from '../../shared/api/api';
import LoadingSpinner from '../../shared/components/LoadingSpinner';
import './BusinessTransactionDetailPage.css';

/**
 * Business Transaction detail screen.
 * Shows full transaction details including request/response bodies.
 */
function BusinessTransactionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showRequestBody, setShowRequestBody] = useState(false);
  const [showResponseBody, setShowResponseBody] = useState(false);

  useEffect(() => {
    fetchTransaction();
  }, [id]);

  const fetchTransaction = async () => {
    setLoading(true);
    try {
      const response = await getBusinessTransaction(id);
      setTransaction(response.data);
    } catch {
      setError('Failed to load transaction details.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusCategory = (code) => {
    if (!code) return 'unknown';
    if (code >= 200 && code < 300) return 'success';
    if (code >= 400 && code < 500) return 'client-error';
    if (code >= 500) return 'server-error';
    return 'other';
  };

  const formatDuration = (ms) => {
    if (ms == null) return '—';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const formatJson = (str) => {
    if (!str) return null;
    try {
      return JSON.stringify(JSON.parse(str), null, 2);
    } catch {
      return str;
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <LoadingSpinner message="Loading transaction details..." />
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="page-container">
        <div className="alert alert-error">{error || 'Transaction not found.'}</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <button
        className="btn-back"
        onClick={() => navigate(location.state?.returnTo || '/business-transactions')}
      >
        ← Back to Transactions
      </button>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Overview card */}
      <div className="card btd-card">
        <div className="btd-header">
          <div className="btd-header-left">
            <h1 className="btd-title">
              <span className={`bt-method bt-method--${(transaction.httpMethod || '').toLowerCase()}`}>
                {transaction.httpMethod || '—'}
              </span>
              <span className="btd-endpoint">{transaction.endpoint || '—'}</span>
            </h1>
            <p className="btd-subtitle">
              <span className="bt-component-badge">{transaction.componentName}</span>
            </p>
          </div>
          <span className={`btd-status-code bt-status-code bt-status-code--${getStatusCategory(transaction.statusCode)}`}>
            {transaction.statusCode || '—'}
          </span>
        </div>

        <div className="btd-fields">
          <DetailField label="ID" value={transaction.id} mono />
          <DetailField label="Transaction ID" value={transaction.transactionId} mono />
          <DetailField label="Correlation ID" value={transaction.correlationId} mono />
          <DetailField label="User ID" value={transaction.userId} />
          <DetailField label="Duration" value={formatDuration(transaction.durationMs)} mono />
          <DetailField
            label="Timestamp"
            value={transaction.timestamp ? new Date(transaction.timestamp).toLocaleString() : '—'}
          />
          <DetailField label="Component" value={transaction.componentName} />
          <DetailField label="Environment" value={transaction.environment} />
          <DetailField label="Tenant ID" value={transaction.tenantId} mono />
          <DetailField label="Bucket Name" value={transaction.bucketName} mono />
          {transaction.logType && (
            <div className="btd-field">
              <span className="btd-field-label">Log Type</span>
              <span className={`bt-log-type bt-log-type--${transaction.logType.toLowerCase()}`}>
                {transaction.logType}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Request body */}
      {transaction.requestBody && (
        <div className="card btd-body-card">
          <button
            className="btd-body-toggle"
            onClick={() => setShowRequestBody((v) => !v)}
          >
            <h3 className="section-title" style={{ marginBottom: 0 }}>Request Body</h3>
            <span className="btd-toggle-icon">{showRequestBody ? '▼' : '▶'}</span>
          </button>
          {showRequestBody && (
            <pre className="btd-code-block">{formatJson(transaction.requestBody)}</pre>
          )}
        </div>
      )}

      {/* Response body */}
      {transaction.responseBody && (
        <div className="card btd-body-card">
          <button
            className="btd-body-toggle"
            onClick={() => setShowResponseBody((v) => !v)}
          >
            <h3 className="section-title" style={{ marginBottom: 0 }}>Response Body</h3>
            <span className="btd-toggle-icon">{showResponseBody ? '▼' : '▶'}</span>
          </button>
          {showResponseBody && (
            <pre className="btd-code-block">{formatJson(transaction.responseBody)}</pre>
          )}
        </div>
      )}
    </div>
  );
}

function DetailField({ label, value, mono }) {
  return (
    <div className="btd-field">
      <span className="btd-field-label">{label}</span>
      <span className={`btd-field-value ${mono ? 'btd-field-mono' : ''}`}>
        {value || '—'}
      </span>
    </div>
  );
}

export default BusinessTransactionDetailPage;
